import { getSupabase } from './auth';

export class ApiError extends Error {
  constructor(message: string, public status: number, public body?: unknown) { super(message); this.name = 'ApiError'; }
}
export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const supabase = getSupabase();
  if (!supabase) throw new ApiError('Connect a care account to use the shared portal.', 401);
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new ApiError('Sign in again to sync your records.', 401);
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${data.session.access_token}`);
  if (options.body && !(options.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
  const route = path.startsWith('/api/') ? path : `/api/v1${path.startsWith('/') ? path : `/${path}`}`;
  const controller = new AbortController();
  const abort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) abort();
  else options.signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(() => controller.abort(), 30_000);
  try {
    const response = await fetch(`${base}${route}`, { ...options, headers, signal: controller.signal, cache: 'no-store' });
    const isJSON = /(?:application\/json|\+json)/i.test(response.headers.get('Content-Type') || '');
    const body: unknown = response.status === 204 ? undefined : isJSON ? await response.json().catch(() => undefined) : await response.blob();
    const errorMessage = body && typeof body === 'object' && 'error' in body && typeof body.error === 'string' ? body.error : undefined;
    if (!response.ok) throw new ApiError(errorMessage || (response.status === 403 ? 'Access to this care record has been removed.' : 'The care service could not complete this request.'), response.status, body);
    return body as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Connection interrupted. Records already saved on this device remain available.', 0);
  } finally { clearTimeout(timeout); options.signal?.removeEventListener('abort', abort); }
}
